import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FEATURES } from '../config/features';
import { 
  ChevronLeft, Upload, User, MapPin, Calendar, 
  AlertTriangle, Phone, Mail, X, Image, CheckCircle, ShieldAlert
} from 'lucide-react';
import axios from 'axios';
import { DataSecurityConsent } from '../components/DataSecurityConsent';

const SCAM_ACK_KEY = 'misyou.scamWarningAcknowledged';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function ReportCasePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  
  const [formData, setFormData] = useState({
    full_name: '',
    age: '',
    date_of_birth: '',
    gender: 'male',
    physical_description: {
      height: '',
      weight: '',
      hair_color: '',
      eye_color: '',
      distinguishing_marks: '',
      build: ''
    },
    last_seen_location: {
      address: '',
      city: '',
      state: '',
      country: '',
      latitude: null,
      longitude: null
    },
    last_seen_date: '',
    last_seen_time: '',
    clothing_description: '',
    medical_conditions: '',
    additional_info: '',
    is_urgent: false,
    is_child: false,
    is_elderly: false,
    reward_amount: '',
    contact_phone: '',
    contact_email: '',
    police_station: '',
    police_reporting_date: '',
    fir_number: '',
    fir_document: '',   // base64 data URI for the FIR copy (mandatory)

    // Legal compliance — age-aware
    reporter_relationship: '',                  // e.g. parent/sibling/spouse/friend
    is_legal_guardian_declared: false,          // only for minors (<18)
    guardianship_document: '',                  // base64 — only for minors
    guardianship_document_type: '',             // birth_certificate | aadhaar | school_id | other
    publish_photo_consent: false,               // parental consent to publish photo — minors only
    photo_visibility_choice: 'show',            // 'show' (default) | 'blur' — minors only
    not_voluntarily_missing_declared: false,    // for adults (18+)
    young_adult_grounds: ''                     // for 18–25 only
  });
  const [firFileName, setFirFileName] = useState('');
  const [guardianshipFileName, setGuardianshipFileName] = useState('');
  const [dataSecurityConsent, setDataSecurityConsent] = useState(false);
  const [showScamWarning, setShowScamWarning] = useState(false);
  // Tracks whether the user has tried to submit. Once true, every empty
  // mandatory field renders a red "Please fill this mandatory field"
  // message so the user can see exactly what is blocking submission.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Returns a dict { fieldName: true } of every mandatory field that is
  // currently empty. Used to highlight missing fields after the user
  // clicks submit.
  const computeMissingFields = () => {
    const missing = {};
    if (!formData.full_name || !formData.full_name.trim()) missing.full_name = true;
    if (!formData.gender) missing.gender = true;
    if (!formData.last_seen_location.city || !formData.last_seen_location.city.trim()) {
      missing.city = true;
    }
    if (!formData.police_station || !formData.police_station.trim()) {
      missing.police_station = true;
    }
    if (!formData.police_reporting_date) missing.police_reporting_date = true;
    if (!formData.fir_document) missing.fir_document = true;
    if (!formData.reporter_relationship || !formData.reporter_relationship.trim()) {
      missing.reporter_relationship = true;
    }
    const ageNum = formData.age ? parseInt(formData.age) : null;
    if (ageNum !== null && !Number.isNaN(ageNum)) {
      if (ageNum < 18) {
        if (!formData.is_legal_guardian_declared) missing.is_legal_guardian_declared = true;
        if (!formData.guardianship_document) missing.guardianship_document = true;
        if (!formData.publish_photo_consent) missing.publish_photo_consent = true;
      } else {
        if (!formData.not_voluntarily_missing_declared) missing.not_voluntarily_missing_declared = true;
        if (ageNum >= 18 && ageNum <= 25 && (formData.young_adult_grounds || '').trim().length < 20) {
          missing.young_adult_grounds = true;
        }
      }
    }
    if (!dataSecurityConsent) missing.dataSecurityConsent = true;
    return missing;
  };

  // Per-step mandatory field check (also drives the red error markers
  // and lets the Continue button surface them instead of silently
  // staying disabled).
  const stepMissingFields = (s) => {
    const m = {};
    if (s === 1) {
      if (!formData.full_name || !formData.full_name.trim()) m.full_name = true;
      if (!formData.gender) m.gender = true;
    } else if (s === 3) {
      if (!formData.last_seen_location.city || !formData.last_seen_location.city.trim()) {
        m.city = true;
      }
    }
    return m;
  };

  const missingFields = submitAttempted
    ? { ...computeMissingFields(), ...stepMissingFields(step) }
    : {};

  // The step a given missing field lives on — used to jump the user back
  // to where they need to fix the field.
  const stepOfField = (field) => {
    if (['full_name', 'gender'].includes(field)) return 1;
    if (field === 'city') return 3;
    return 4;
  };

  useEffect(() => {
    if (!localStorage.getItem(SCAM_ACK_KEY)) {
      setShowScamWarning(true);
    }
  }, []);

  const acknowledgeScamWarning = () => {
    localStorage.setItem(SCAM_ACK_KEY, new Date().toISOString());
    setShowScamWarning(false);
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate ALL mandatory fields at once so the user sees every missing
    // field highlighted in red, not just the first one. We also jump them
    // to the step containing the first missing field for convenience.
    const missing = computeMissingFields();
    const missingKeys = Object.keys(missing);
    if (missingKeys.length > 0) {
      setSubmitAttempted(true);
      const firstStep = Math.min(...missingKeys.map(stepOfField));
      setStep(firstStep);
      setError(
        missingKeys.length === 1
          ? 'Please fill the mandatory field highlighted in red below.'
          : `Please fill the ${missingKeys.length} mandatory fields highlighted in red below.`,
      );
      // Give React a tick to switch steps before scrolling
      setTimeout(() => {
        const first = document.querySelector('[data-mandatory-error="true"]');
        if (first && typeof first.scrollIntoView === 'function') {
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 80);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        photos,
        age: formData.age ? parseInt(formData.age) : null,
        reward_amount: formData.reward_amount ? parseFloat(formData.reward_amount) : null
      };

      await axios.post(`${API}/cases`, payload, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Failed to submit case. Please try again.';
      if (status === 451) {
        // Unavailable For Legal Reasons — CSAM quarantine path.
        // We deliberately do NOT reveal our detection mechanism; just show
        // the friendly rejection message the backend returned.
        setError(detail);
      } else if (typeof detail === 'string' && detail.toLowerCase().includes('free limit')) {
        setError(`${detail}  Click here to subscribe →`);
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const prevStep = () => setStep(step - 1);

  // Per-step mandatory field check. The actual `stepMissingFields`
  // helper lives next to `missingFields` above so React renders see
  // it during the same pass.

  // Try to advance — if the current step has missing mandatory fields,
  // surface them in red instead of silently doing nothing.
  const handleNext = () => {
    const missing = stepMissingFields(step);
    if (Object.keys(missing).length > 0) {
      setSubmitAttempted(true);
      setError(
        Object.keys(missing).length === 1
          ? 'Please fill the mandatory field highlighted in red below.'
          : `Please fill the ${Object.keys(missing).length} mandatory fields highlighted in red below.`,
      );
      setTimeout(() => {
        const first = document.querySelector('[data-mandatory-error="true"]');
        if (first && typeof first.scrollIntoView === 'function') {
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 80);
      return;
    }
    setError('');
    setStep(step + 1);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Case Submitted Successfully</h2>
            <p className="text-muted-foreground mb-4">
              Your case has been reported. We'll notify nearby users to help find your loved one.
            </p>
            <p className="text-sm text-muted-foreground">Redirecting to dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Blink animation for "Please fill this mandatory field" markers.
          Kept short and accessible (1.2s loop, no flash above 3Hz) so it
          attracts attention without triggering vestibular issues. */}
      <style>{`
        @keyframes misyouMandatoryBlink {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.35; }
        }
        .misyou-mandatory-error {
          color: #b91c1c;
          font-weight: 600;
          font-size: 0.75rem;
          margin-top: 0.25rem;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          animation: misyouMandatoryBlink 1.2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .misyou-mandatory-error { animation: none; }
        }
        .misyou-mandatory-ring {
          box-shadow: 0 0 0 2px #ef4444 !important;
          border-color: #ef4444 !important;
        }
      `}</style>
      {/* First-time-poster scam warning modal */}
      <Dialog open={showScamWarning} onOpenChange={(o) => { if (!o) acknowledgeScamWarning(); }}>
        <DialogContent className="max-w-lg" data-testid="scam-warning-modal">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-2">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl">Before you post — a safety note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-700">
            <p>
              <strong>⚠ If anyone calls demanding money for information, it can be a scam.</strong>
              Real police and hospitals will <strong>never</strong> ask for payment to share information about a missing person.
            </p>
            <p>
              If you receive such a call, click <strong>Report Abuse</strong> on your case page to alert our team —
              <strong> and also report it to your local police station immediately.</strong>
            </p>
            <p className="text-base sm:text-lg font-bold text-red-900 leading-snug bg-red-50 border-l-4 border-red-600 px-3 py-3 rounded">
              MISYOU advises you — Don&apos;t give any money to anyone without involving your local police station, always keep your local police informed.
            </p>
            <p className="text-xs text-slate-500">
              MISYOU will never display your phone number publicly. Sighting tips reach you through our secure in-app system.
            </p>
          </div>
          <Button
            className="w-full bg-red-600 hover:bg-red-700"
            size="lg"
            onClick={acknowledgeScamWarning}
            data-testid="scam-warning-ack-btn"
          >
            I understand &mdash; continue
          </Button>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-muted-foreground hover:text-primary">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-lg font-black text-slate-900">MISYOU</span>
              </Link>
            </div>
            <span className="text-sm text-muted-foreground">Step {step} of 4</span>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`flex-1 h-2 rounded-full ${
                  s <= step ? 'bg-primary' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span className={step >= 1 ? 'text-primary font-medium' : ''}>Basic Info</span>
            <span className={step >= 2 ? 'text-primary font-medium' : ''}>Description</span>
            <span className={step >= 3 ? 'text-primary font-medium' : ''}>Last Seen</span>
            <span className={step >= 4 ? 'text-primary font-medium' : ''}>Contact</span>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <Card className="shadow-xl" data-testid="step-1">
              <CardHeader>
                <CardTitle className="text-2xl">Basic Information</CardTitle>
                <CardDescription>Enter the missing person's details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Photo Upload */}
                <div>
                  <Label className="mb-3 block">Photos</Label>
                  <div className="flex flex-wrap gap-4 mb-3">
                    {photos.map((photo, index) => (
                      <div key={photo.slice(0, 64)} className="relative w-24 h-24">
                        <img 
                          src={photo} 
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <label className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                      <Image className="w-6 h-6 text-slate-400" />
                      <span className="text-xs text-muted-foreground mt-1">Add Photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handlePhotoUpload}
                        data-testid="photo-upload"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">Upload recent, clear photos of the missing person</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      placeholder="Enter full name"
                      className={`mt-1 ${missingFields.full_name ? 'misyou-mandatory-ring' : ''}`}
                      required
                      data-testid="input-full-name"
                    />
                    {missingFields.full_name && (
                      <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-full-name">
                        <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="age">Age</Label>
                    <Input
                      id="age"
                      type="number"
                      value={formData.age}
                      onChange={(e) => handleChange('age', e.target.value)}
                      placeholder="Age"
                      className="mt-1"
                      data-testid="input-age"
                    />
                  </div>

                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => handleChange('date_of_birth', e.target.value)}
                      className="mt-1"
                      data-testid="input-dob"
                    />
                  </div>

                  <div>
                    <Label>Gender *</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger
                        className={`mt-1 ${missingFields.gender ? 'misyou-mandatory-ring' : ''}`}
                        data-testid="select-gender"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {missingFields.gender && (
                      <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-gender">
                        <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                      </p>
                    )}
                  </div>
                </div>

                {/* Priority Flags */}
                <div className="pt-4 border-t border-slate-200">
                  <Label className="mb-3 block">Priority Flags</Label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.is_urgent}
                        onCheckedChange={(v) => handleChange('is_urgent', v)}
                        data-testid="check-urgent"
                      />
                      <span className="text-sm">Urgent Case</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.is_child}
                        onCheckedChange={(v) => handleChange('is_child', v)}
                        data-testid="check-child"
                      />
                      <span className="text-sm">Child (under 18)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.is_elderly}
                        onCheckedChange={(v) => handleChange('is_elderly', v)}
                        data-testid="check-elderly"
                      />
                      <span className="text-sm">Elderly (65+)</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Physical Description */}
          {step === 2 && (
            <Card className="shadow-xl" data-testid="step-2">
              <CardHeader>
                <CardTitle className="text-2xl">Physical Description</CardTitle>
                <CardDescription>Help others identify the person</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Height</Label>
                    <Input
                      value={formData.physical_description.height}
                      onChange={(e) => handleChange('physical_description.height', e.target.value)}
                      placeholder="e.g., 5ft 8in or 173 cm"
                      className="mt-1"
                      data-testid="input-height"
                    />
                  </div>
                  <div>
                    <Label>Weight</Label>
                    <Input
                      value={formData.physical_description.weight}
                      onChange={(e) => handleChange('physical_description.weight', e.target.value)}
                      placeholder="e.g., 150 lbs or 68 kg"
                      className="mt-1"
                      data-testid="input-weight"
                    />
                  </div>
                  <div>
                    <Label>Hair Color</Label>
                    <Input
                      value={formData.physical_description.hair_color}
                      onChange={(e) => handleChange('physical_description.hair_color', e.target.value)}
                      placeholder="e.g., Brown, Black"
                      className="mt-1"
                      data-testid="input-hair"
                    />
                  </div>
                  <div>
                    <Label>Eye Color</Label>
                    <Input
                      value={formData.physical_description.eye_color}
                      onChange={(e) => handleChange('physical_description.eye_color', e.target.value)}
                      placeholder="e.g., Blue, Brown"
                      className="mt-1"
                      data-testid="input-eyes"
                    />
                  </div>
                  <div>
                    <Label>Build</Label>
                    <Select 
                      value={formData.physical_description.build} 
                      onValueChange={(v) => handleChange('physical_description.build', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select build" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="slim">Slim</SelectItem>
                        <SelectItem value="average">Average</SelectItem>
                        <SelectItem value="athletic">Athletic</SelectItem>
                        <SelectItem value="heavy">Heavy</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Distinguishing Marks</Label>
                  <Textarea
                    value={formData.physical_description.distinguishing_marks}
                    onChange={(e) => handleChange('physical_description.distinguishing_marks', e.target.value)}
                    placeholder="Scars, tattoos, birthmarks, etc."
                    className="mt-1"
                    rows={3}
                    data-testid="input-marks"
                  />
                </div>
                <div>
                  <Label>Clothing When Last Seen</Label>
                  <Textarea
                    value={formData.clothing_description}
                    onChange={(e) => handleChange('clothing_description', e.target.value)}
                    placeholder="Describe what they were wearing"
                    className="mt-1"
                    rows={3}
                    data-testid="input-clothing"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Last Seen Location */}
          {step === 3 && (
            <Card className="shadow-xl" data-testid="step-3">
              <CardHeader>
                <CardTitle className="text-2xl">Last Known Location</CardTitle>
                <CardDescription>Where was the person last seen?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Street Address</Label>
                  <Input
                    value={formData.last_seen_location.address}
                    onChange={(e) => handleChange('last_seen_location.address', e.target.value)}
                    placeholder="Street address or landmark"
                    className="mt-1"
                    data-testid="input-address"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={formData.last_seen_location.city}
                      onChange={(e) => handleChange('last_seen_location.city', e.target.value)}
                      placeholder="City"
                      className={`mt-1 ${missingFields.city ? 'misyou-mandatory-ring' : ''}`}
                      required
                      data-testid="input-city"
                    />
                    {missingFields.city && (
                      <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-city">
                        <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>State/Province</Label>
                    <Input
                      value={formData.last_seen_location.state}
                      onChange={(e) => handleChange('last_seen_location.state', e.target.value)}
                      placeholder="State or Province"
                      className="mt-1"
                      data-testid="input-state"
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input
                      value={formData.last_seen_location.country}
                      onChange={(e) => handleChange('last_seen_location.country', e.target.value)}
                      placeholder="Country"
                      className="mt-1"
                      data-testid="input-country"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                  <div>
                    <Label>Date Last Seen</Label>
                    <Input
                      type="date"
                      value={formData.last_seen_date}
                      onChange={(e) => handleChange('last_seen_date', e.target.value)}
                      className="mt-1"
                      data-testid="input-last-seen-date"
                    />
                  </div>
                  <div>
                    <Label>Time Last Seen</Label>
                    <Input
                      type="time"
                      value={formData.last_seen_time}
                      onChange={(e) => handleChange('last_seen_time', e.target.value)}
                      className="mt-1"
                      data-testid="input-last-seen-time"
                    />
                  </div>
                </div>
                <div>
                  <Label>Medical Conditions</Label>
                  <Textarea
                    value={formData.medical_conditions}
                    onChange={(e) => handleChange('medical_conditions', e.target.value)}
                    placeholder="Any medical conditions that require attention"
                    className="mt-1"
                    rows={3}
                    data-testid="input-medical"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Contact & Submit */}
          {step === 4 && (
            <Card className="shadow-xl" data-testid="step-4">
              <CardHeader>
                <CardTitle className="text-2xl">Police Report &amp; Contact Information</CardTitle>
                <CardDescription>Confirm police reporting (required) and how to reach you.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">

                {/* Police reporting — mandatory */}
                <div className="rounded-xl border-2 border-red-200 bg-red-50/40 p-4">
                  <p className="text-sm font-bold text-red-900 mb-2 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" /> Police reporting (mandatory)
                  </p>
                  <p className="text-xs text-red-800 mb-3">
                    You must report any missing person to your local police station <strong>before</strong> posting on MISYOU.
                    This protects you, your family, and the legitimacy of our platform.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="police-station">Police station where reported <span className="text-red-600">*</span></Label>
                      <Input
                        id="police-station"
                        value={formData.police_station}
                        onChange={(e) => handleChange('police_station', e.target.value)}
                        placeholder="e.g., Talwandi Sabo Police Station"
                        required
                        className={missingFields.police_station ? 'misyou-mandatory-ring' : ''}
                        data-testid="input-police-station"
                      />
                      {missingFields.police_station && (
                        <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-police-station">
                          <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="police-date">Date of reporting <span className="text-red-600">*</span></Label>
                      <Input
                        id="police-date"
                        type="date"
                        value={formData.police_reporting_date}
                        onChange={(e) => handleChange('police_reporting_date', e.target.value)}
                        required
                        className={missingFields.police_reporting_date ? 'misyou-mandatory-ring' : ''}
                        data-testid="input-police-date"
                      />
                      {missingFields.police_reporting_date && (
                        <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-police-date">
                          <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label htmlFor="fir-number">FIR Number (optional)</Label>
                    <Input
                      id="fir-number"
                      value={formData.fir_number}
                      onChange={(e) => handleChange('fir_number', e.target.value)}
                      placeholder="e.g., 123/2026"
                      data-testid="input-fir-number"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      You can add this later once your FIR is registered. Helps authorities cross-reference.
                    </p>
                  </div>

                  {/* Mandatory FIR copy upload — proves the case is genuinely registered */}
                  <div className="mt-4" data-testid="fir-upload-section">
                    <Label htmlFor="fir-document">
                      Copy of FIR <span className="text-red-600">*</span>
                    </Label>
                    <p className="text-xs text-slate-500 mt-1 mb-2">
                      Upload a clear scan or photo of the FIR you filed at the police station. Accepted formats: JPG, PNG, WEBP, PDF (max 5 MB). This is mandatory before your case can be posted.
                    </p>
                    <Input
                      id="fir-document"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        if (!file) {
                          setFirFileName('');
                          handleChange('fir_document', '');
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          setError('FIR file is too large. Please upload a file under 5 MB.');
                          e.target.value = '';
                          return;
                        }
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          handleChange('fir_document', reader.result || '');
                          setFirFileName(file.name);
                          setError('');
                        };
                        reader.readAsDataURL(file);
                      }}
                      required
                      data-testid="input-fir-document"
                    />
                    {firFileName && (
                      <p
                        className="text-xs text-emerald-700 mt-2"
                        data-testid="fir-upload-filename"
                      >
                        Attached: {firFileName}
                      </p>
                    )}
                    {missingFields.fir_document && (
                      <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-fir-document">
                        <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                      </p>
                    )}
                  </div>
                </div>

                {/* ─────────────────────────────────────────────────────────
                    Legal compliance — relationship + age-aware consent
                    ───────────────────────────────────────────────────────── */}
                {(() => {
                  const ageNum = formData.age ? parseInt(formData.age) : null;
                  const isMinor = ageNum !== null && !Number.isNaN(ageNum) && ageNum < 18;
                  const isAdult = ageNum !== null && !Number.isNaN(ageNum) && ageNum >= 18;
                  const isYoungAdult = ageNum !== null && ageNum >= 18 && ageNum <= 25;
                  return (
                    <div
                      className="rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-4"
                      data-testid="legal-compliance-section"
                    >
                      <p className="text-sm font-bold text-indigo-900 mb-2 flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4" /> Legal compliance
                      </p>
                      <p className="text-xs text-indigo-800 mb-3">
                        These declarations protect children and respect the legal rights of adults. False declarations may attract legal action under IPC / IT Act.
                      </p>

                      {/* Relationship — always required */}
                      <div className="mb-4">
                        <Label htmlFor="reporter-relationship">
                          Your relationship to the missing person <span className="text-red-600">*</span>
                        </Label>
                        <Select
                          value={formData.reporter_relationship}
                          onValueChange={(v) => handleChange('reporter_relationship', v)}
                        >
                          <SelectTrigger
                            className={`mt-1 ${missingFields.reporter_relationship ? 'misyou-mandatory-ring' : ''}`}
                            data-testid="select-reporter-relationship"
                          >
                            <SelectValue placeholder="Select your relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="parent">Parent (mother / father)</SelectItem>
                            <SelectItem value="legal_guardian">Court-appointed legal guardian</SelectItem>
                            <SelectItem value="spouse">Spouse</SelectItem>
                            <SelectItem value="sibling">Sibling</SelectItem>
                            <SelectItem value="child">Adult child (reporting parent)</SelectItem>
                            <SelectItem value="grandparent">Grandparent</SelectItem>
                            <SelectItem value="extended_family">Other family (uncle / aunt / cousin)</SelectItem>
                            <SelectItem value="friend">Close friend</SelectItem>
                            <SelectItem value="employer">Employer / colleague</SelectItem>
                            <SelectItem value="ngo">NGO / shelter representative</SelectItem>
                            <SelectItem value="self">Reporting myself (subject is me)</SelectItem>
                          </SelectContent>
                        </Select>
                        {missingFields.reporter_relationship && (
                          <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-reporter-relationship">
                            <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                          </p>
                        )}
                      </div>

                      {/* MINORS (age < 18) — guardianship gate */}
                      {isMinor && (
                        <div
                          className="rounded-lg border border-purple-300 bg-purple-50 p-3 mb-2"
                          data-testid="minor-guardianship-block"
                        >
                          <p className="text-sm font-bold text-purple-900 mb-2">
                            Missing child (under 18)
                          </p>
                          <p className="text-xs text-purple-800 mb-3">
                            A moderator will review the document you upload. Until then, your case will be visible publicly with a “Guardianship under review” badge. If verification fails, the case will be removed.
                          </p>

                          <label className="flex items-start gap-2 mb-3 cursor-pointer">
                            <Checkbox
                              checked={formData.is_legal_guardian_declared}
                              onCheckedChange={(v) => handleChange('is_legal_guardian_declared', !!v)}
                              data-testid="checkbox-legal-guardian"
                              className={missingFields.is_legal_guardian_declared ? 'misyou-mandatory-ring' : ''}
                            />
                            <span className="text-xs text-purple-900 leading-relaxed">
                              I declare that I am the <strong>legal guardian</strong> of this child (parent, court-appointed guardian, or legally authorised relative) and have the right to file this report. I understand a false declaration is a punishable offence.
                            </span>
                          </label>
                          {missingFields.is_legal_guardian_declared && (
                            <p className="misyou-mandatory-error mb-2" data-mandatory-error="true" data-testid="error-legal-guardian">
                              <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                            </p>
                          )}

                          <div className="mb-2">
                            <Label htmlFor="guardianship-doc-type">Type of guardianship proof</Label>
                            <Select
                              value={formData.guardianship_document_type}
                              onValueChange={(v) => handleChange('guardianship_document_type', v)}
                            >
                              <SelectTrigger className="mt-1" data-testid="select-guardianship-doc-type">
                                <SelectValue placeholder="Select document type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="birth_certificate">Birth certificate</SelectItem>
                                <SelectItem value="aadhaar">Aadhaar (with parent name)</SelectItem>
                                <SelectItem value="school_id">School ID with parent name</SelectItem>
                                <SelectItem value="court_order">Court guardianship order</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="guardianship-document">
                              Upload guardianship document <span className="text-red-600">*</span>
                            </Label>
                            <p className="text-xs text-purple-700 mt-1 mb-2">
                              JPG, PNG, WEBP or PDF, max 5 MB. Only moderators can view this — it is never shown publicly.
                            </p>
                            <Input
                              id="guardianship-document"
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                if (!file) {
                                  setGuardianshipFileName('');
                                  handleChange('guardianship_document', '');
                                  return;
                                }
                                if (file.size > 5 * 1024 * 1024) {
                                  setError('Guardianship file is too large. Please upload a file under 5 MB.');
                                  e.target.value = '';
                                  return;
                                }
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  handleChange('guardianship_document', reader.result || '');
                                  setGuardianshipFileName(file.name);
                                  setError('');
                                };
                                reader.readAsDataURL(file);
                              }}
                              data-testid="input-guardianship-document"
                              className={missingFields.guardianship_document ? 'misyou-mandatory-ring' : ''}
                            />
                            {guardianshipFileName && (
                              <p
                                className="text-xs text-emerald-700 mt-2"
                                data-testid="guardianship-upload-filename"
                              >
                                Attached: {guardianshipFileName}
                              </p>
                            )}
                            {missingFields.guardianship_document && (
                              <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-guardianship-document">
                                <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                              </p>
                            )}
                          </div>

                          {/* Parental consent to publish + face-visibility choice.
                              Legal basis for making the child's photo publicly
                              visible; distinct from guardianship declaration
                              (which asserts legal status). Timestamp + IP are
                              captured server-side on submit for the audit trail. */}
                          <div className="mt-4 pt-4 border-t border-purple-200">
                            <label
                              className="flex items-start gap-2 mb-3 cursor-pointer"
                              data-testid="publish-photo-consent-label"
                            >
                              <Checkbox
                                checked={formData.publish_photo_consent}
                                onCheckedChange={(v) => handleChange('publish_photo_consent', !!v)}
                                data-testid="checkbox-publish-photo-consent"
                                className={missingFields.publish_photo_consent ? 'misyou-mandatory-ring' : ''}
                              />
                              <span className="text-xs text-purple-900 leading-relaxed">
                                I am the legal parent/guardian and I authorise MISYOU to
                                <strong> publish this child&apos;s photo and identifying details </strong>
                                for the sole purpose of aiding their safe return. I understand
                                this listing may be viewed publicly (like a newspaper missing-child ad
                                or the Delhi Police ZIPNET portal).
                              </span>
                            </label>
                            {missingFields.publish_photo_consent && (
                              <p
                                className="misyou-mandatory-error mb-3"
                                data-mandatory-error="true"
                                data-testid="error-publish-photo-consent"
                              >
                                <AlertTriangle className="w-3 h-3" /> Please tick this consent to publish
                              </p>
                            )}

                            <div className="mt-1">
                              <Label className="text-xs font-semibold text-purple-900">
                                How would you like the child&apos;s face to appear publicly?
                              </Label>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                <label
                                  className={`flex items-start gap-2 p-2 border rounded-md cursor-pointer transition ${
                                    formData.photo_visibility_choice === 'show'
                                      ? 'border-purple-500 bg-white ring-1 ring-purple-500'
                                      : 'border-purple-200 bg-white/40 hover:bg-white'
                                  }`}
                                  data-testid="photo-visibility-show-label"
                                >
                                  <input
                                    type="radio"
                                    name="photo_visibility_choice"
                                    value="show"
                                    checked={formData.photo_visibility_choice === 'show'}
                                    onChange={() => handleChange('photo_visibility_choice', 'show')}
                                    className="mt-1"
                                    data-testid="radio-photo-visibility-show"
                                  />
                                  <span className="text-xs text-purple-900 leading-relaxed">
                                    <strong>Show face publicly</strong>{' '}
                                    <span className="text-[10px] uppercase tracking-wider text-purple-700">Recommended</span>
                                    <br />
                                    <span className="text-purple-700">
                                      Maximises the chance the community will recognise your child.
                                      Other faces in the photo (siblings, bystanders) are auto-blurred for their privacy.
                                    </span>
                                  </span>
                                </label>
                                <label
                                  className={`flex items-start gap-2 p-2 border rounded-md cursor-pointer transition ${
                                    formData.photo_visibility_choice === 'blur'
                                      ? 'border-purple-500 bg-white ring-1 ring-purple-500'
                                      : 'border-purple-200 bg-white/40 hover:bg-white'
                                  }`}
                                  data-testid="photo-visibility-blur-label"
                                >
                                  <input
                                    type="radio"
                                    name="photo_visibility_choice"
                                    value="blur"
                                    checked={formData.photo_visibility_choice === 'blur'}
                                    onChange={() => handleChange('photo_visibility_choice', 'blur')}
                                    className="mt-1"
                                    data-testid="radio-photo-visibility-blur"
                                  />
                                  <span className="text-xs text-purple-900 leading-relaxed">
                                    <strong>Blur face — only police can see</strong>
                                    <br />
                                    <span className="text-purple-700">
                                      Face blurred publicly. Verified authorities can request an
                                      unmasked copy through our documented workflow. Note: this
                                      reduces the chance of community recognition.
                                    </span>
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ADULTS (age >= 18) — voluntary-disappearance gate */}
                      {isAdult && (
                        <div
                          className="rounded-lg border border-amber-300 bg-amber-50 p-3"
                          data-testid="adult-consent-block"
                        >
                          <p className="text-sm font-bold text-amber-900 mb-2">
                            Adult (18+): legal rights notice
                          </p>
                          <p className="text-xs text-amber-800 mb-3">
                            Under Indian law, an adult has the right to leave home of their own free will, even without informing family. A missing-person listing must <strong>not</strong> be used to track an adult who has left voluntarily.
                          </p>

                          <label className="flex items-start gap-2 mb-3 cursor-pointer">
                            <Checkbox
                              checked={formData.not_voluntarily_missing_declared}
                              onCheckedChange={(v) => handleChange('not_voluntarily_missing_declared', !!v)}
                              data-testid="checkbox-not-voluntary"
                              className={missingFields.not_voluntarily_missing_declared ? 'misyou-mandatory-ring' : ''}
                            />
                            <span className="text-xs text-amber-900 leading-relaxed">
                              I declare that, to the best of my knowledge, this person <strong>did not leave voluntarily</strong>. There are reasonable grounds (medical condition, foul play, dependents left behind, prior threats, etc.) to believe they are at risk.
                            </span>
                          </label>
                          {missingFields.not_voluntarily_missing_declared && (
                            <p className="misyou-mandatory-error mb-2" data-mandatory-error="true" data-testid="error-not-voluntary">
                              <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field
                            </p>
                          )}

                          {isYoungAdult && (
                            <div data-testid="young-adult-grounds-block">
                              <Label htmlFor="young-adult-grounds">
                                Reasonable grounds (ages 18–25) <span className="text-red-600">*</span>
                              </Label>
                              <Textarea
                                id="young-adult-grounds"
                                value={formData.young_adult_grounds}
                                onChange={(e) => handleChange('young_adult_grounds', e.target.value)}
                                placeholder="E.g., She has Type-1 diabetes and her insulin is still at home; or, he had received threats the week before."
                                className={`mt-1 ${missingFields.young_adult_grounds ? 'misyou-mandatory-ring' : ''}`}
                                rows={3}
                                data-testid="input-young-adult-grounds"
                              />
                              <p className="text-xs text-amber-700 mt-1">
                                Required for ages 18–25 (the highest-risk age band for misuse). Minimum 20 characters.
                              </p>
                              {missingFields.young_adult_grounds && (
                                <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-young-adult-grounds">
                                  <AlertTriangle className="w-3 h-3" /> Please fill this Mandatory Field (at least 20 characters)
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {ageNum === null && (
                        <p className="text-xs text-slate-500 italic">
                          Tip: enter the missing person’s age in Step 1 — extra protections kick in for minors (under 18) and young adults (18–25).
                        </p>
                      )}
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Phone</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => handleChange('contact_phone', e.target.value)}
                        placeholder="+91 9876543210"
                        className="pl-10"
                        data-testid="input-contact-phone"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Kept private. Never shown publicly.</p>
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => handleChange('contact_email', e.target.value)}
                        placeholder="your@email.com"
                        className="pl-10"
                        data-testid="input-contact-email"
                      />
                    </div>
                  </div>
                </div>
                {FEATURES.SHOW_REWARDS && (
                  <div>
                    <Label>Reward Amount (Optional)</Label>
                    <Input
                      type="number"
                      value={formData.reward_amount}
                      onChange={(e) => handleChange('reward_amount', e.target.value)}
                      placeholder="Enter reward amount"
                      className="mt-1"
                      data-testid="input-reward"
                    />
                  </div>
                )}
                <div>
                  <Label>Additional Information</Label>
                  <Textarea
                    value={formData.additional_info}
                    onChange={(e) => handleChange('additional_info', e.target.value)}
                    placeholder="Any other details that might help find this person"
                    className="mt-1"
                    rows={4}
                    data-testid="input-additional"
                  />
                </div>

                {/* Privacy Notice */}
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    The information you provide will be publicly visible to help find the missing person. 
                    Contact information will be shown to potential witnesses.
                  </AlertDescription>
                </Alert>

                {/* Data Security Consent */}
                <DataSecurityConsent 
                  checked={dataSecurityConsent}
                  onCheckedChange={setDataSecurityConsent}
                  className="mt-6"
                />
                {missingFields.dataSecurityConsent && (
                  <p className="misyou-mandatory-error" data-mandatory-error="true" data-testid="error-data-security-consent">
                    <AlertTriangle className="w-3 h-3" /> Please tick the Data Security &amp; Liability Waiver above
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-6">
            {step > 1 && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={prevStep}
                className="rounded-full px-6"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            )}
            {step < 4 ? (
              <Button 
                type="button" 
                onClick={handleNext}
                className="ml-auto rounded-full px-6 btn-press"
                data-testid="next-step-btn"
              >
                Continue
              </Button>
            ) : (
              <Button 
                type="submit"
                disabled={loading}
                className="ml-auto bg-red-600 hover:bg-red-700 rounded-full px-8 btn-press"
                data-testid="submit-case-btn"
              >
                {loading ? 'Submitting...' : 'Submit Case'}
              </Button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}
